import jwtDecode from 'jwt-decode';
import OidcService from '../services/oidc.service';
import AuthModule from './AuthModule';

/**
 * Sisi frontend login OIDC fe-ellenkasquad.
 *
 * CATATAN SENGAJA TENTANG DUPLIKASI: `terapkanSesi` menyimpan sesi ke
 * localStorage dengan cara yang sama seperti jalur SSO lama di `App/App.js`.
 * Jalur lama itu SENGAJA tidak disentuh -- ia dipakai seluruh pengguna asset
 * setiap hari, dan menyatukan keduanya sekarang berarti mempertaruhkan login
 * semua orang demi kerapian. Begitu OIDC jadi satu-satunya jalur, blok lama itu
 * DIHAPUS -- bukan digabungkan -- dan duplikasi ini hilang sendirinya.
 *
 * 🔴 Bentuk sesi BERBEDA antar-aplikasi LNK. Di fe-ellenkasquad `roles` adalah
 * ARRAY peran (`res.role`, masing-masing membawa `role_code` + `segment_code`
 * + gambar segmen), peran default disimpan sebagai `active_role`, dan menu
 * diminta per peran DAN segmen (`getMenuByRoleAndSegment`) -- persis seperti
 * login ulang berbekal cookie SSO di `App/App.js`. Menyalin versi aplikasi lain
 * apa adanya membuat menu gagal secara halus: menu kosong, bukan galat.
 *
 * Akun guest/mitra tidak pernah lewat sini: pengalihan ke OP hanya terjadi di
 * cabang yang sudah membuktikan cookie SSO yang sah.
 */

const aktif = () => OidcService.aktif();

/** Mulai login: navigasi penuh ke be-ellenkasquad, yang meneruskan ke OP. */
const urlLoginUntukHalamanIni = () => {
	const kembaliKe = `${window.location.origin}${window.location.pathname}`;
	return OidcService.urlLogin(kembaliKe);
};

/** Tiket yang ditempelkan callback be-ellenkasquad di URL, bila ada. */
const ambilTiketDariUrl = (search) => {
	const params = new URLSearchParams(
		typeof search === 'string' ? search : window.location.search,
	);
	return params.get(OidcService.PARAM_TIKET);
};

/**
 * Buang parameter tiket dari address bar tanpa memuat ulang halaman. Tiket
 * memang sudah tidak berlaku setelah ditebus, tapi membiarkannya di URL membuat
 * pengguna bisa mem-bookmark atau membagikan tautan yang menyesatkan -- dan
 * tautan itu akan gagal, karena tiketnya sekali pakai.
 */
const bersihkanTiketDariUrl = () => {
	const url = new URL(window.location.href);
	if (!url.searchParams.has(OidcService.PARAM_TIKET)) return null;
	url.searchParams.delete(OidcService.PARAM_TIKET);
	const bersih = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState({}, '', bersih);
	return bersih;
};

/**
 * Pesan galat yang ditempelkan callback be-ellenkasquad saat login OIDC gagal, bila ada.
 *
 * Callback itu didatangi NAVIGASI BROWSER, jadi dulu kegagalan dijawab JSON dan
 * pengguna melihat `{"error":true,"message":...}` polos di layar kosong.
 * Sekarang callback memulangkan pengguna ke sini dengan `?oidc_error=`, dan
 * aplikasilah yang menampilkannya sebagai dialog.
 *
 * Yang menentukan adalah KEHADIRAN parameternya, bukan isinya. Parameter yang
 * ada tapi kosong tetap berarti "login gagal" dan dijawab pesan umum yang sama
 * dengan fallback be-ellenkasquad. Kalau dianggap tidak ada, alurnya mengalihkan ke OP
 * lagi -- dan repo ini tidak punya pagar waktu yang bisa menahan lingkarannya.
 */
const PESAN_GALAT_UMUM = 'Login OIDC gagal';

const ambilGalatDariUrl = (search) => {
	const params = new URLSearchParams(
		typeof search === 'string' ? search : window.location.search,
	);
	if (!params.has(OidcService.PARAM_GALAT)) return null;
	const pesan = (params.get(OidcService.PARAM_GALAT) || '').trim();
	return pesan || PESAN_GALAT_UMUM;
};

/**
 * Buang parameter galat dari address bar tanpa memuat ulang halaman.
 *
 * Wajib dilakukan begitu galatnya dibaca: kalau dibiarkan, memuat ulang halaman
 * memunculkan dialog yang sama lagi, dan tautan yang ter-bookmark membawa pesan
 * galat yang sudah tidak relevan.
 */
const bersihkanGalatDariUrl = () => {
	const url = new URL(window.location.href);
	if (!url.searchParams.has(OidcService.PARAM_GALAT)) return null;
	url.searchParams.delete(OidcService.PARAM_GALAT);
	const bersih = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState({}, '', bersih);
	return bersih;
};

const tebusTiket = (ticket) => OidcService.redeem(ticket);

/**
 * OIDC hanya boleh dimulai dari jendela TERATAS.
 *
 * Bila halaman ini kebetulan dimuat di dalam frame (mis. WebView/iframe pihak
 * lain), OIDC tidak boleh dimulai dari situ. OpenID Provider One LNK mengirim
 * `x-frame-options: SAMEORIGIN` dan CSP `frame-ancestors 'self'` -- diperiksa
 * langsung pada halaman `interaction`-nya -- jadi halaman loginnya TIDAK akan
 * dirender di dalam frame milik domain lain. Tanpa pagar ini, pengguna yang
 * memakai fe-ellenkasquad dalam mode embed hanya melihat frame kosong, tanpa galat.
 *
 * Di dalam frame kita jatuh kembali ke jalur login lama, yang tetap bekerja.
 */
const bisaDiJendelaIni = () => {
	try {
		return window.self === window.top;
	} catch (e) {
		// Membaca window.top dari frame lintas-origin bisa melempar; kejadian itu
		// sendiri sudah berarti kita berada di dalam frame.
		return false;
	}
};

/** Kunci localStorage yang ditulis `terapkanSesi` -- dibuang lagi bila menunya gagal. */
const KUNCI_SESI = ['accessToken', 'roles', 'active_role', 'photo', 'menu'];

/**
 * Simpan sesi hasil penebusan tiket. Mengembalikan true bila sesinya dipakai.
 *
 * Meniru login ulang berbekal cookie SSO di `App/App.js` langkah demi langkah:
 * `accessToken`, `roles` (array peran), `active_role` dari peran default,
 * `getMenuByRoleAndSegment(role_code, segment_code)`, lalu `photo`.
 *
 * Mengembalikan false (dan TIDAK menyimpan apa pun) bila sesinya tidak layak:
 * tanpa `accessToken`, tanpa `current_person`, atau tanpa peran default. Jalur
 * lama sendiri melempar TypeError pada kasus terakhir (`new_roles[0]`
 * undefined) sesudah setengah sesi tersimpan; di sini ia ditolak sebelum apa
 * pun ditulis.
 *
 * `accessToken` disimpan SEBELUM menu diminta, sama seperti jalur lama
 * (`authHeader()` membacanya). Kalau menu gagal, semua kunci yang baru ditulis
 * dibuang lagi -- pengguna tidak "masuk" dengan sesi setengah jadi.
 */
const terapkanSesi = async (sesi) => {
	if (!sesi?.accessToken) return false;

	const isiToken = jwtDecode(sesi.accessToken);
	if (!isiToken?.details?.hris_org_tree?.current_person) return false;

	const peranDefault = [...(sesi.role || [])].find((p) => p?.is_default);
	if (!peranDefault) return false;

	localStorage.setItem('accessToken', JSON.stringify(sesi));
	localStorage.setItem('roles', JSON.stringify(sesi.role));
	localStorage.setItem(
		'active_role',
		JSON.stringify({
			default_role_name: peranDefault.role_name,
			default_role_code: peranDefault.role_code,
			default_segment_code: peranDefault.segment_code,
			default_segment_name: peranDefault.segment_name,
		}),
	);
	try {
		await AuthModule.getMenuByRoleAndSegment(peranDefault.role_code, peranDefault.segment_code);
	} catch (e) {
		KUNCI_SESI.forEach((kunci) => localStorage.removeItem(kunci));
		throw e;
	}
	localStorage.setItem('photo', JSON.stringify({ ...sesi?.photo }));
	return true;
};

/**
 * Apakah sesi yang sedang berjalan lahir dari OIDC?
 *
 * Penandanya `oidc_sid` di dalam token — hanya sesi yang diterbitkan lewat OP
 * yang punya itu. Sesi jalur lama tidak, dan perilakunya tidak boleh berubah.
 */
const sesiDariOidc = () => {
	try {
		const simpanan = JSON.parse(localStorage.getItem('accessToken'));
		if (!simpanan?.accessToken) return false;
		return Boolean(jwtDecode(simpanan.accessToken)?.oidc_sid);
	} catch (e) {
		// Penyimpanan rusak/kosong atau token bukan JWT. Jatuh ke jalur logout
		// lama, bukan melempar di dalam penangan klik tombol logout.
		return false;
	}
};

/**
 * Ke mana logout harus menuju: URL `end_session` bila sesi ini memang lahir
 * dari OIDC, atau null supaya pemanggil memakai jalur logout lamanya.
 *
 * Dipisah dari komponen supaya keputusannya bisa diuji tanpa merender apa pun.
 */
const tujuanLogout = () => (aktif() && sesiDariOidc() ? OidcService.urlLogout() : null);

export default {
	aktif,
	urlLoginUntukHalamanIni,
	ambilTiketDariUrl,
	bersihkanTiketDariUrl,
	ambilGalatDariUrl,
	bersihkanGalatDariUrl,
	tebusTiket,
	terapkanSesi,
	bisaDiJendelaIni,
	sesiDariOidc,
	tujuanLogout,
};
